import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9os9hgsenbniolesksg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快回复您！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();

      if (response.ok) {
        let parsed;
        try { parsed = JSON.parse(responseText); } catch { parsed = null; }
        if (parsed?.code === 'OK') {
          setFormStatus('success');
          setFormMessage('感谢您的留言！我们会尽快回复您。');
          form.reset();
        } else {
          const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
          if (String(serverMsg).includes('spam') || String(serverMsg).includes('form data is spam')) {
            setFormStatus('error');
            setFormMessage('提交失败，请稍后重试。');
          } else {
            setFormStatus('error');
            setFormMessage(String(serverMsg) || '提交失败，请稍后重试。');
          }
        }
      } else {
        setFormStatus('error');
        setFormMessage('网络错误，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 text-center">
            关于韩漫大全
          </h1>
          <p className="mt-4 text-foreground-600 text-center max-w-2xl mx-auto">
            专注于韩国漫画的中文在线阅读平台，为韩漫爱好者提供无删减、高质量的免费阅读体验
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-foreground-900">品牌使命</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                让每一位韩漫爱好者都能随时随地享受高质量的韩国漫画阅读体验，打破语言和地域的限制。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                <i className="ri-eye-line text-2xl"></i>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-foreground-900">品牌愿景</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                成为全球最大的韩国漫画中文阅读平台，建立完善的漫画生态系统，连接创作者与读者。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-foreground-900">核心价值观</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                正版授权、无删减内容、用户至上、持续创新。我们尊重每一位创作者和每一位读者。
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-18">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
              品牌故事
            </h2>
            <div className="mt-8 space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                韩漫大全成立于2020年，由一群热爱韩国漫画的中国年轻人创立。我们发现在国内寻找高质量的韩漫资源十分困难——要么内容被删减，要么翻译质量参差不齐，阅读体验更是难以保证。于是，我们决定打造一个真正以用户为中心的韩漫阅读平台。
              </p>
              <p>
                经过六年的发展，韩漫大全已经积累了超过24,000部韩国漫画资源，与韩国多家知名漫画平台建立了合作关系，拥有专业的翻译和编辑团队。我们坚持提供无删减的完整内容，支持下拉式阅读，让读者能够沉浸式地享受每一部漫画。
              </p>
              <p>
                我们的团队分布在北京和首尔两地，核心成员包括资深漫画编辑、韩语翻译专家和技术工程师。我们不仅关注内容的丰富性，更重视阅读体验的每一个细节——从页面加载速度到文字排版，从分类导航到个性化推荐，每一个环节都在不断优化。
              </p>
              <p>
                韩漫大全的成长离不开每一位读者的支持。未来，我们将继续扩展漫画资源库，引入更多优质的韩国漫画作品，并为创作者和读者搭建更紧密的连接桥梁。
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-18">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
            用户口碑
          </h2>
          <p className="mt-3 text-foreground-600 text-center text-sm">
            来自韩漫爱好者的真实评价
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
              <div className="flex items-center gap-1 text-accent-500">
                {[1, 2, 3, 4, 5].map((n) => (
                  <i key={n} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                "画质很清晰，翻译质量也很高，下拉式阅读体验太棒了！终于找到了一个能放心看韩漫的平台。"
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold">
                  小
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">小琳同学</p>
                  <p className="text-xs text-foreground-500">韩漫爱好者 · 3年老用户</p>
                </div>
              </div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
              <div className="flex items-center gap-1 text-accent-500">
                {[1, 2, 3, 4, 5].map((n) => (
                  <i key={n} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                "分类很全，更新速度也快。热门漫画基本能做到同步更新，再也不用到处找资源了。"
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-sm font-bold">
                  阿
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">阿杰读书</p>
                  <p className="text-xs text-foreground-500">漫画博主 · 2年老用户</p>
                </div>
              </div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
              <div className="flex items-center gap-1 text-accent-500">
                {[1, 2, 3, 4, 5].map((n) => (
                  <i key={n} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                "无删减这一点真的很重要！很多平台看到的都是删减版，只有韩漫大全保持了原汁原味的内容。"
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 text-sm font-bold">
                  樱
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">樱花漫语</p>
                  <p className="text-xs text-foreground-500">在校大学生 · 1年老用户</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="bg-background-100 py-14 md:py-18">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
              联系我们
            </h2>
            <p className="mt-3 text-foreground-600 text-center text-sm">
              有任何问题或建议？请随时与我们联系
            </p>

            <div className="mt-8 bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <p className="mt-2 text-sm font-medium text-foreground-900">电子邮箱</p>
                  <p className="text-xs text-foreground-600">support@manhua-kr.com</p>
                </div>
                <div className="text-center">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-500">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <p className="mt-2 text-sm font-medium text-foreground-900">微信公众号</p>
                  <p className="text-xs text-foreground-600">韩漫大全官方</p>
                </div>
                <div className="text-center">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <p className="mt-2 text-sm font-medium text-foreground-900">工作时间</p>
                  <p className="text-xs text-foreground-600">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                {formMessage && (
                  <div className={`p-3 rounded-lg text-sm ${
                    formStatus === 'success'
                      ? 'bg-accent-100 text-accent-800 border border-accent-200'
                      : 'bg-primary-100 text-primary-800 border border-primary-200'
                  }`}>
                    {formMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  {formStatus === 'loading' ? '提交中...' : '提交留言'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}